import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const label = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleDateString(undefined, { month: "short", year: "2-digit" });

const Tip = ({ active, payload }) =>
  active && payload?.length ? (
    <div className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs">
      <p className="text-slate-400">{payload[0].payload.label}</p>
      <p className="font-mono text-amber-300 font-bold">{payload[0].value} certificates</p>
    </div>
  ) : null;

export default function ActivityPanel() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [storeId, setStoreId] = useState("all");

  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null);
    setErr("");
    api.get("/admin/activity", { params: { months } }).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  }, [months]);

  const series = useMemo(() => {
    if (!data) return [];
    const counts = storeId === "all" ? data.totals : data.stores.find((s) => s.id === storeId)?.counts || [];
    return data.months.map((m, i) => ({ label: label(m), n: counts[i] || 0 }));
  }, [data, storeId]);

  const total = series.reduce((a, b) => a + b.n, 0);
  const last = series.at(-1)?.n || 0;
  const prev = series.at(-2)?.n || 0;

  return (
    <div className="space-y-6" data-testid="activity-panel">
      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-heading font-bold flex items-center gap-2"><TrendingUp size={16} className="text-amber-400" /> Certificates created per month</p>
            <p className="text-xs text-slate-500 mt-1" data-testid="activity-summary">
              {total} in the last {months} months · this month {last}{prev ? ` (${last >= prev ? "+" : ""}${Math.round(((last - prev) / prev) * 100)}% vs last month)` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <select className="cf-input h-8 text-xs" style={{ width: 220 }} value={storeId} onChange={(e) => setStoreId(e.target.value)} data-testid="activity-store-select">
              <option value="all">All stores</option>
              {data?.stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className="cf-input h-8 text-xs" style={{ width: 120 }} value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="activity-months-select">
              {[3, 6, 12].map((m) => <option key={m} value={m}>Last {m} months</option>)}
            </select>
          </div>
        </div>
        <div className="h-72 mt-6" data-testid="activity-chart">
          {data ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ left: -10, right: 10 }}>
                <CartesianGrid stroke="#1E293B" vertical={false} />
                <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<Tip />} cursor={{ fill: "rgba(245,158,11,0.06)" }} />
                <Bar dataKey="n" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className={`h-full grid place-items-center text-sm ${err ? "text-red-400" : "text-slate-500"}`} data-testid="activity-status">{err || "Loading…"}</p>}
        </div>
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-900/60">
        <p className="font-heading font-bold px-5 py-4 border-b border-slate-800">Most active stores</p>
        {data && !data.stores.length && <p className="p-8 text-center text-sm text-slate-500" data-testid="activity-empty">No certificates in this period.</p>}
        {data && data.stores.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="activity-table">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="px-5 py-3">Store</th>
                  {data.months.map((m) => <th key={m} className="px-3 py-3 text-right">{label(m)}</th>)}
                  <th className="px-5 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.stores.slice(0, 15).map((s, i) => {
                  const max = Math.max(...s.counts, 1);
                  return (
                    <tr key={s.id} className={`border-b border-slate-800/60 cursor-pointer hover:bg-slate-800/30 ${storeId === s.id ? "bg-amber-500/5" : ""}`} onClick={() => setStoreId(s.id)} data-testid={`activity-row-${i}`}>
                      <td className="px-5 py-2.5"><p className="font-semibold text-slate-200">{s.name}</p><p className="font-mono text-[10px] text-slate-500">{s.email}</p></td>
                      {s.counts.map((n, j) => (
                        <td key={j} className="px-3 py-2.5 text-right font-mono">
                          <span className="inline-block min-w-[28px] rounded px-1.5 py-0.5" style={{ background: n ? `rgba(245,158,11,${0.1 + (n / max) * 0.4})` : "transparent", color: n ? "#FDE68A" : "#475569" }}>{n}</span>
                        </td>
                      ))}
                      <td className="px-5 py-2.5 text-right font-mono font-bold text-amber-300">{s.total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
